import {
  extractShopifyId,
  getLineItemDiscountPrice,
  getDisplayPriceCart,
  getTotalDiscountAmount,
  getCollectionArray,
  storeCartDataToLocalStore,
} from './cart-api-utils';
import { CartFragmentFragment } from './cart-api-queries-mutations/CartFragment.shopify.generated';
import { CartLineFragmentFragment } from './cart-api-queries-mutations/CartLine.shopify.generated';
import { Attributes } from '.';
import { CartWarningFragmentFragment } from 'src/generated/shopify-graphql';

const convertToProperties = (item: CartLineFragmentFragment) => {
  const properties = {} as Attributes;
  item?.node?.attributes?.forEach((attribute) => {
    switch (attribute.key) {
      case '_isGWP':
      case '__shopify_send_gift_card_to_recipient':
        properties[attribute.key] = attribute.value === 'true';
        break;
      case '_loyalty_reward_claim_id':
      case '_loyalty_reward_id':
      case '_loyalty_reward_point_cost':
        properties[attribute.key] = Number(attribute.value);
        break;
      case '_loyalty_reward_expiry_date':
        properties[attribute.key] = attribute.value?.toString() ?? undefined; // Ensure string
        break;
      case '__lion_context':
      case '_market':
      case 'Recipient email':
      case 'Recipient name':
      case 'Message':
      case '_setName':
      case '_setUid':
      case '_setUrl':
      case 'Send on':
        properties[attribute.key] = attribute.value ?? undefined;
        break;
      default:
        break;
    }
  });
  return properties;
};

function isProductSoldOutByCode(warnings: CartWarningFragmentFragment[], targetToMatch: string): boolean {
  return warnings.some((warning) => warning.target === targetToMatch && warning.code === 'MERCHANDISE_OUT_OF_STOCK');
}

export const transformCartApiLineItems = (input: CartFragmentFragment | undefined | null, cartInfo?: CartInfo) => {
  const lineItems = input?.lines.edges;
  const warnings = cartInfo?.warnings || [];
  const items =
    lineItems &&
    lineItems.map((item) => {
      const isProductSoldOutWarningExist = isProductSoldOutByCode(warnings, item.node.id);
      const quantity = item.node.quantity;
      const lineItemActualPrice = quantity === 0 ? 0 : (Number(item.node.cost.totalAmount.amount) / quantity) * 100;
      const totalActualPrice = Number(item.node.cost.totalAmount.amount) * 100;
      const amountPerQuantity = Number(item.node.cost.amountPerQuantity.amount) * 100;

      const lineItemSubTotalAmount =
        quantity === 0 ? 0 : (Number(item.node.cost.subtotalAmount.amount) / quantity) * 100;
      const subTotalAmount = Number(item.node.cost.subtotalAmount.amount) * 100;

      const variantId = Number(extractShopifyId(item.node.merchandise.id));
      const productId = extractShopifyId(item.node.merchandise.product.id);
      const displayPrice = getDisplayPriceCart(item.node.cost.compareAtAmountPerQuantity?.amount);
      const tags = item.node.merchandise.product.tags;
      const product_title = item.node.merchandise.product.title;
      const title = product_title + item.node.merchandise.title;
      const options_with_values = item.node.merchandise.selectedOptions;
      const product_type = item.node.merchandise.product.productType;
      const handle = item.node.merchandise.product.handle;
      const url = `/products/${handle}?variant=${variantId}`;
      const sku = item.node.merchandise.sku || '';
      const image = item.node.merchandise.image?.url;
      const vendor = item.node.merchandise.product.vendor;
      const requires_shipping = item.node.merchandise.requiresShipping;
      const featured_image = {
        alt: item.node.merchandise.image?.altText || '',
        url: image,
      };
      const product_description = item.node.merchandise.product.description;
      const variant_options = item.node.merchandise.selectedOptions.map((option) => option.value);
      const quantityAvailable = isProductSoldOutWarningExist ? 0 : item.node.merchandise.quantityAvailable;
      const properties = convertToProperties(item);
      const discounted_price = getLineItemDiscountPrice(item);
      const collections = getCollectionArray(item.node.merchandise);
      const estimatedShipDate = item.node.merchandise.estimatedShipDate?.value || null;
      const gift_card = item.node.merchandise.product.isGiftCard;
      const gatedSalePrice = Number(item.node.merchandise.price.amount) * quantity * 100;
      const gatedSaleSavings = totalActualPrice - gatedSalePrice;

      return {
        gatedSalePrice,
        gatedSaleSavings,
        displayPrice: displayPrice,
        featured_image,
        final_line_price: totalActualPrice,
        final_price: lineItemActualPrice,
        handle,
        id: variantId ? Number(variantId) : 0,
        image,
        key: `${variantId}:${item.node.id}`,
        options_with_values,
        original_line_price: subTotalAmount,
        original_price: lineItemSubTotalAmount,
        presentment_price: lineItemSubTotalAmount / 100,
        price: lineItemSubTotalAmount ?? 0,
        product_description,
        product_id: Number(productId),
        product_title,
        product_type,
        quantity,
        requires_shipping,
        sku,
        tags,
        collections,
        title,
        url,
        variant_id: variantId,
        variant_options,
        variant_title: item.node.merchandise.title,
        vendor,

        discounted_price: lineItemActualPrice,
        discounts: [{ amount: discounted_price }],
        gift_card,
        line_level_discount_allocations: [{ amount: discounted_price }],
        line_level_total_discount: discounted_price,
        line_price: totalActualPrice,
        properties,
        total_discount: discounted_price,
        untranslated_product_title: product_title,
        untranslated_variant_title: product_title,
        quantityAvailable: quantityAvailable ?? undefined,
        estimatedShipDate,
        line_id: item.node.id,
        amountPerQuantity,
      };
    });

  const data = {
    items: items ? items : [],
  };
  return data;
};

export const transformCartApiResponse = (input: CartFragmentFragment | undefined | null, cartInfo?: CartInfo) => {
  const cart = input;
  const { items } = transformCartApiLineItems(cart, cartInfo);
  const transformedInput = {
    attributes: cart?.attributes ?? null,
    currency: window?.Shopify?.currency?.active ?? 'USD',
    item_count: cart?.totalQuantity || 0,
    items,
    items_subtotal_price: Number(cart?.cost.subtotalAmount?.amount) * 100 || 0,
    note: cart?.note || null,
    original_total_price: cart?.cost.totalAmount?.amount || 0,
    token: '', // #ToDo: As Part Of Cart API: This attribute is not getting from Cart API's
    total_discount: getTotalDiscountAmount(input) || 0,
    total_price: Number(cart?.cost.totalAmount?.amount) * 100 || 0,
    checkoutUrl: cart?.checkoutUrl,
  };
  storeCartDataToLocalStore(transformedInput);
  return transformedInput;
};

interface CartInfo {
  warnings?: CartWarningFragmentFragment[];
}
